// VoiceCloningStudio.jsx — Phase 6: Voice Cloning Studio
// Full studio: upload calls, diarize, isolate agent voice, IVC/PVC, A/B compare,
// inflection tags, attach to bots.

import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mic, Play, Upload, Trash2, RefreshCw, Volume2, Sparkles, Info,
  CheckCircle2, XCircle, Loader2, Tag, Sliders, Bot, Plus,
  AlertTriangle, ChevronRight, FlaskConical, Users, AudioLines,
  Check, X, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, api } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    collecting: { label: 'Collecting', cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
    training:   { label: 'Training',   cls: 'bg-blue-500/20  text-blue-300  border-blue-500/30'  },
    ready:      { label: 'Ready',      cls: 'bg-green-500/20 text-green-300 border-green-500/30' },
    failed:     { label: 'Failed',     cls: 'bg-red-500/20   text-red-300   border-red-500/30'   },
  };
  const s = map[status] || { label: status, cls: 'bg-white/10 text-white/50 border-white/10' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

// ─── Waveform mini-viz ────────────────────────────────────────────────────────

function WaveformBar({ durationSec, qualityScore }) {
  const bars = 20;
  return (
    <div className="flex items-end gap-0.5 h-8">
      {Array.from({ length: bars }).map((_, i) => {
        const h = 20 + Math.sin(i * 0.8 + (qualityScore || 50) * 0.05) * 14 + Math.random() * 8;
        return (
          <div
            key={i}
            className="w-1 rounded-xs bg-civix-purple/60"
            style={{ height: `${h}%` }}
          />
        );
      })}
    </div>
  );
}

// ─── AssemblyAI banner ────────────────────────────────────────────────────────

function AssemblyAIBanner({ configured }) {
  if (configured) return null;
  return (
    <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-sm">
      <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
      <div>
        <span className="font-semibold text-yellow-300">Manual speaker mode active.</span>
        <span className="text-white/60 ml-1">
          Add <code className="bg-black/30 px-1 rounded-sm">ASSEMBLYAI_API_KEY</code> to Render environment variables
          for automatic speaker detection. Currently, after ingesting a call you&apos;ll need to manually select
          which transcript segments belong to the agent.
        </span>
      </div>
    </div>
  );
}

// ─── Profile list page ────────────────────────────────────────────────────────

function ProfileList({ onSelect, onNew }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['voice-profiles'],
    queryFn:  () => apiGet('/voice-profiles'),
    staleTime: 10000,
  });

  const { data: asmData } = useQuery({
    queryKey: ['assemblyai-status'],
    queryFn:  () => apiGet('/voice-profiles/meta/assemblyai-status'),
    staleTime: 60000,
  });

  const profiles = data?.data || data || [];
  const asmConfigured = asmData?.data?.configured ?? false;

  return (
    <div className="space-y-4">
      <AssemblyAIBanner configured={asmConfigured} />

      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/60 text-sm">
            Upload calls, isolate your voice via diarization, train a custom ElevenLabs clone.
          </p>
        </div>
        <button onClick={onNew} className="btn-primary flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> New Profile
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 text-civix-purple animate-spin" />
        </div>
      ) : profiles.length === 0 ? (
        <div className="panel p-12 text-center space-y-4">
          <AudioLines className="w-10 h-10 text-civix-purple/50 mx-auto" />
          <div>
            <p className="text-white font-semibold">No voice profiles yet</p>
            <p className="text-white/40 text-sm mt-1">
              Create a profile, upload calls, and train your agent&apos;s AI voice.
            </p>
          </div>
          <button onClick={onNew} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create Voice Profile
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {profiles.map(p => (
            <button
              key={p._id}
              onClick={() => onSelect(p._id)}
              className="panel p-4 text-left hover:border-civix-purple/40 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-civix-purple/20 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-civix-purple" />
                  </div>
                  <div>
                    <div className="font-semibold text-white group-hover:text-civix-cyan transition-colors">
                      {p.name}
                    </div>
                    <div className="text-xs text-white/40 mt-0.5">
                      {p.targetAgentName && <span className="mr-2">Agent: {p.targetAgentName}</span>}
                      {p.cloneType?.toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <div className="text-xs text-white/40">
                    <div>{p.sampleCount || 0} samples</div>
                    <div>{p.attachedBotCount || 0} bots</div>
                  </div>
                  <StatusBadge status={p.cloneStatus} />
                  <ChevronRight className="w-4 h-4 text-white/30" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── New profile form ─────────────────────────────────────────────────────────

function NewProfileForm({ onCreated, onCancel }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', description: '', targetAgentName: '', cloneType: 'ivc' });

  const { data: asmData } = useQuery({
    queryKey: ['assemblyai-status'],
    queryFn:  () => apiGet('/voice-profiles/meta/assemblyai-status'),
  });

  const mutation = useMutation({
    mutationFn: () => apiPost('/voice-profiles', form),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['voice-profiles'] });
      toast.success('Voice profile created!');
      onCreated(r?.data?._id || r?._id);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to create profile'),
  });

  return (
    <div className="panel p-6 max-w-lg space-y-5">
      <h2 className="text-lg font-bold text-white flex items-center gap-2">
        <Plus className="w-5 h-5 text-civix-purple" /> New Voice Profile
      </h2>

      <div className="space-y-4">
        <div>
          <label className="form-label">Profile name *</label>
          <input
            className="input w-full"
            placeholder="e.g. Easton's Voice"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>

        <div>
          <label className="form-label">Agent name to isolate</label>
          <input
            className="input w-full"
            placeholder="e.g. Easton — whose voice to find in calls"
            value={form.targetAgentName}
            onChange={e => setForm(f => ({ ...f, targetAgentName: e.target.value }))}
          />
          <p className="text-xs text-white/40 mt-1">
            AI will look for this person&apos;s speaking patterns during diarization.
          </p>
        </div>

        <div>
          <label className="form-label">Description</label>
          <input
            className="input w-full"
            placeholder="Optional notes"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>

        <div>
          <label className="form-label">Clone type</label>
          <div className="grid grid-cols-2 gap-2">
            {['ivc', 'pvc'].map(t => (
              <button
                key={t}
                onClick={() => setForm(f => ({ ...f, cloneType: t }))}
                className={`p-3 rounded-xl border text-sm transition-all ${
                  form.cloneType === t
                    ? 'border-civix-purple bg-civix-purple/20 text-white'
                    : 'border-civix-border text-white/50 hover:border-civix-purple/40'
                }`}
              >
                <div className="font-semibold">{t.toUpperCase()}</div>
                <div className="text-xs opacity-70 mt-0.5">
                  {t === 'ivc' ? 'Instant — fast, ≤25 samples' : 'Professional — 3+ hrs audio'}
                </div>
                {t === 'pvc' && (
                  <div className="text-xs text-yellow-400 mt-1">
                    {(asmData?.data?.configured) ? '1/1 PVC slots used — check ElevenLabs' : 'Requires Pro+'}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={onCancel} className="btn-ghost">Cancel</button>
        <button
          onClick={() => mutation.mutate()}
          disabled={!form.name || mutation.isPending}
          className="btn-primary flex items-center gap-2"
        >
          {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create Profile
        </button>
      </div>
    </div>
  );
}

// ─── Profile detail with tabs ─────────────────────────────────────────────────

function ProfileDetail({ profileId, onBack }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState('samples');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['voice-profile', profileId],
    queryFn:  () => apiGet(`/voice-profiles/${profileId}`),
    staleTime: 5000,
    refetchInterval: (d) => {
      const status = d?.data?.cloneStatus;
      return status === 'training' ? 3000 : false;
    },
    retry: false,
  });

  const profile = data?.data || data || {};

  const TABS = [
    { id: 'samples',       label: 'Samples',       icon: AudioLines },
    { id: 'diarization',   label: 'Diarization',   icon: Users },
    { id: 'inflections',   label: 'Inflections',   icon: Tag },
    { id: 'clone',         label: 'Clone',         icon: Sparkles },
    { id: 'ab',            label: 'A/B Compare',   icon: FlaskConical },
    { id: 'attach',        label: 'Attach to Bot', icon: Bot },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 text-civix-purple animate-spin" />
      </div>
    );
  }

  if (isError || !profile?._id) {
    const status = error?.response?.status;
    const serverMsg = error?.response?.data?.error;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="btn-ghost text-sm px-2 py-1">← Back</button>
        </div>
        <div className="panel p-8 text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto" />
          <div>
            <p className="text-white font-semibold">Could not load voice profile</p>
            <p className="text-white/50 text-sm mt-1">
              {status ? `${status}: ` : ''}{serverMsg || error?.message || 'The profile may have been deleted.'}
            </p>
          </div>
          <div className="flex gap-2 justify-center">
            <button onClick={refetch} className="btn-secondary text-sm">Retry</button>
            <button onClick={onBack} className="btn-ghost text-sm">Back to list</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="btn-ghost text-sm px-2 py-1">← Back</button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white">{profile.name}</h1>
            <StatusBadge status={profile.cloneStatus} />
          </div>
          {profile.targetAgentName && (
            <p className="text-white/50 text-sm">Isolating: {profile.targetAgentName}</p>
          )}
        </div>
        <button onClick={refetch} className="btn-ghost text-xs">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Training progress bar */}
      {profile.cloneStatus === 'training' && (
        <div className="panel p-4 flex items-center gap-4">
          <Loader2 className="w-5 h-5 text-civix-cyan animate-spin shrink-0" />
          <div className="flex-1">
            <div className="flex justify-between mb-1">
              <span className="text-sm text-white">Training in progress...</span>
              <span className="text-sm text-civix-cyan">{profile.trainingProgress || 0}%</span>
            </div>
            <div className="h-2 bg-civix-border rounded-full overflow-hidden">
              <div
                className="h-full bg-civix-gradient transition-all duration-500"
                style={{ width: `${profile.trainingProgress || 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-civix-purple/20 text-white border border-civix-purple/40'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {tab === 'samples'     && <SamplesTab     profile={profile} profileId={profileId} refetch={refetch} qc={qc} />}
      {tab === 'diarization' && <DiarizationTab profile={profile} profileId={profileId} refetch={refetch} qc={qc} />}
      {tab === 'inflections' && <InflectionsTab profile={profile} profileId={profileId} refetch={refetch} qc={qc} />}
      {tab === 'clone'       && <CloneTab       profile={profile} profileId={profileId} refetch={refetch} qc={qc} />}
      {tab === 'ab'          && <ABTab          profile={profile} profileId={profileId} refetch={refetch} qc={qc} />}
      {tab === 'attach'      && <AttachTab      profile={profile} profileId={profileId} refetch={refetch} qc={qc} />}
    </div>
  );
}

// ─── Samples tab ──────────────────────────────────────────────────────────────

function SamplesTab({ profile, profileId, refetch, qc }) {
  const [ingestUrl, setIngestUrl] = useState('');
  const [callId, setCallId] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const ingestMutation = useMutation({
    mutationFn: ({ audioUrl, sourceCallId }) =>
      apiPost(`/voice-profiles/${profileId}/ingest`, { audioUrl, sourceCallId }),
    onSuccess: () => {
      toast.success('Diarization started. Reload in a moment to see results.');
      setIngestUrl('');
      setCallId('');
      setTimeout(() => refetch(), 3000);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Ingest failed'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ sampleId, includedInClone }) =>
      api.patch(`/voice-profiles/${profileId}/samples/${sampleId}`, { includedInClone }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voice-profile', profileId] }),
  });

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileRef.current) fileRef.current.value = '';
    setBusy(true);
    try {
      // Real call recordings are 5–50 MB. Sending base64 through express.json
      // would hit the 10 MB body cap ("request entity too large"), so push the
      // file to the multipart upload endpoint (multer→R2) and feed the
      // returned URL into /ingest. `globalThis.FormData` is used explicitly
      // to be robust against any local module-scope `FormData` shadow that
      // would otherwise produce a TDZ error here.
      const FD = globalThis.FormData || window.FormData;
      const fd = new FD();
      fd.append('audio', file, file.name);
      const upload = await api
        .post(`/voice-profiles/${profileId}/samples/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 5 * 60 * 1000, // 5min for slow connections + large files
        })
        .then(r => r.data);
      const audioUrl = upload?.data?.audioUrl;
      if (!audioUrl) throw new Error('Upload succeeded but no URL returned');
      await ingestMutation.mutateAsync({ audioUrl, sourceCallId: file.name });
    } catch (err) {
      // mutateAsync already toasts via onError; surface upload/read errors here too.
      if (!err?.response) toast.error(err?.message || 'File upload failed');
      else if (err.response?.status === 413) toast.error('File too large. Max 100 MB.');
      else toast.error(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  const samples = profile.samples || [];
  const lastError = profile.errorMessage;

  return (
    <div className="space-y-4">
      {lastError && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-sm">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="font-semibold text-red-300">Last ingest error:</span>
            <span className="text-white/70 ml-1">{lastError}</span>
          </div>
        </div>
      )}

      {/* Ingest panel */}
      <div className="panel p-5 space-y-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Upload className="w-4 h-4 text-civix-purple" /> Ingest Call Recording
        </h3>
        <p className="text-sm text-white/50">
          Upload a call recording or paste a URL. The AI will diarize it, identify {profile.targetAgentName || 'the agent'},
          and extract the best voice samples.
        </p>

        <div className="space-y-3">
          <div>
            <label className="form-label">Audio URL (publicly accessible)</label>
            <input
              className="input w-full"
              placeholder="https://... or leave blank to upload file"
              value={ingestUrl}
              onChange={e => setIngestUrl(e.target.value)}
            />
          </div>
          <div className="text-center text-white/30 text-xs">— or —</div>
          <div>
            <input type="file" accept="audio/*" ref={fileRef} className="hidden" onChange={handleFileUpload} />
            <button
              onClick={() => fileRef.current?.click()}
              className="btn-secondary w-full flex items-center justify-center gap-2"
              disabled={busy || ingestMutation.isPending}
            >
              {busy || ingestMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload audio file
            </button>
          </div>
          <div>
            <label className="form-label">Source call ID (optional reference)</label>
            <input
              className="input w-full"
              placeholder="e.g. call-id from CRM"
              value={callId}
              onChange={e => setCallId(e.target.value)}
            />
          </div>
          {ingestUrl && (
            <button
              onClick={() => ingestMutation.mutate({ audioUrl: ingestUrl, sourceCallId: callId })}
              disabled={ingestMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {ingestMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Start Diarization
            </button>
          )}
        </div>
      </div>

      {/* Sample grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-white">
            Samples ({samples.length}) &mdash; {samples.filter(s => s.includedInClone).length} selected for clone
          </h3>
        </div>

        {samples.length === 0 ? (
          <div className="panel p-8 text-center text-white/40">
            No samples yet. Ingest a call to get started.
          </div>
        ) : (
          <div className="grid gap-2">
            {samples.map(s => (
              <div
                key={s._id}
                className={`panel p-4 flex items-center gap-4 transition-all ${
                  s.includedInClone ? 'border-civix-purple/40' : ''
                }`}
              >
                <WaveformBar durationSec={s.durationSec} qualityScore={s.qualityScore} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-white/60">
                      Speaker {s.speakerLabel || '?'}
                    </span>
                    <span className="text-xs text-white/40">{s.durationSec?.toFixed(1)}s</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      s.qualityScore >= 70 ? 'bg-green-500/20 text-green-400' :
                      s.qualityScore >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      Q:{s.qualityScore}
                    </span>
                  </div>
                  <p className="text-xs text-white/60 truncate">{s.transcript || '—'}</p>
                  {s.sourceCallId && (
                    <p className="text-xs text-white/30 mt-0.5">from: {s.sourceCallId}</p>
                  )}
                </div>

                <button
                  onClick={() => toggleMutation.mutate({ sampleId: s._id, includedInClone: !s.includedInClone })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    s.includedInClone
                      ? 'bg-civix-purple/30 text-civix-cyan border border-civix-purple/40'
                      : 'bg-civix-border text-white/40 hover:text-white'
                  }`}
                >
                  {s.includedInClone ? <CheckCircle2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {s.includedInClone ? 'Included' : 'Include'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Diarization review tab ───────────────────────────────────────────────────

function DiarizationTab({ profile, profileId, refetch }) {
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [callIdFilter, setCallIdFilter] = useState('');

  const selectMutation = useMutation({
    mutationFn: ({ speakerLabel }) =>
      apiPost(`/voice-profiles/${profileId}/select-speaker`, {
        speakerLabel,
        sourceCallId: callIdFilter,
      }),
    onSuccess: () => {
      toast.success('Speaker selected — samples marked for clone');
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to select speaker'),
  });

  const samples = profile.samples || [];
  const sourceCalls = [...new Set(samples.map(s => s.sourceCallId).filter(Boolean))];
  const filteredSamples = callIdFilter
    ? samples.filter(s => s.sourceCallId === callIdFilter)
    : samples;

  const speakerColors = { A: 'text-blue-400', B: 'text-green-400', C: 'text-yellow-400', unknown: 'text-white/40' };

  return (
    <div className="space-y-4">
      {profile.diarizationProvider === 'whisper_manual' && (
        <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-sm">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-yellow-300">Manual mode.</span>
            <span className="text-white/60 ml-1">
              AssemblyAI is not configured. All segments are labeled &quot;unknown&quot;.
              Select which speaker is the agent to mark samples for cloning.
            </span>
          </div>
        </div>
      )}

      {sourceCalls.length > 0 && (
        <div>
          <label className="form-label">Filter by source call</label>
          <select
            className="input"
            value={callIdFilter}
            onChange={e => setCallIdFilter(e.target.value)}
          >
            <option value="">All calls</option>
            {sourceCalls.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {/* Speaker selector */}
      <div className="panel p-4 space-y-3">
        <h3 className="font-semibold text-white text-sm">Select agent speaker</h3>
        <p className="text-xs text-white/50">Click the speaker label that matches {profile.targetAgentName || 'the sales agent'}.</p>
        <div className="flex gap-2 flex-wrap">
          {['A', 'B', 'C', 'unknown'].map(sp => {
            const count = filteredSamples.filter(s => s.speakerLabel === sp).length;
            if (!count) return null;
            return (
              <button
                key={sp}
                onClick={() => setSelectedSpeaker(sp)}
                className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                  selectedSpeaker === sp
                    ? 'border-civix-purple bg-civix-purple/20 text-white'
                    : 'border-civix-border text-white/50 hover:border-white/20'
                }`}
              >
                Speaker {sp} ({count})
              </button>
            );
          })}
        </div>
        {selectedSpeaker && (
          <button
            onClick={() => selectMutation.mutate({ speakerLabel: selectedSpeaker })}
            disabled={selectMutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {selectMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Mark Speaker {selectedSpeaker} as Agent
          </button>
        )}
      </div>

      {/* Transcript */}
      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {filteredSamples.map(s => (
          <div
            key={s._id}
            className={`flex gap-3 p-3 rounded-xl border border-civix-border ${
              s.includedInClone ? 'bg-civix-purple/5' : ''
            }`}
          >
            <span className={`text-xs font-mono font-bold shrink-0 mt-0.5 ${speakerColors[s.speakerLabel] || 'text-white/40'}`}>
              [{s.speakerLabel || '?'}]
            </span>
            <div className="flex-1">
              <p className="text-sm text-white/80">{s.transcript || '—'}</p>
              <div className="flex gap-3 mt-1 text-xs text-white/30">
                <span>{s.durationSec?.toFixed(1)}s</span>
                <span>Q:{s.qualityScore}</span>
                {s.includedInClone && <span className="text-green-400">✓ included</span>}
              </div>
            </div>
          </div>
        ))}
        {filteredSamples.length === 0 && (
          <div className="text-center py-8 text-white/40">No samples yet.</div>
        )}
      </div>
    </div>
  );
}

// ─── Inflection tags tab ──────────────────────────────────────────────────────

const INFLECTION_LABELS = [
  'rapport', 'objection-handling', 'closing', 'empathy',
  'enthusiasm', 'pace-shift', 'pause',
];

const INFLECTION_COLORS = {
  'rapport':            'bg-blue-500/20   text-blue-300',
  'objection-handling': 'bg-orange-500/20 text-orange-300',
  'closing':            'bg-green-500/20  text-green-300',
  'empathy':            'bg-purple-500/20 text-purple-300',
  'enthusiasm':         'bg-yellow-500/20 text-yellow-300',
  'pace-shift':         'bg-cyan-500/20   text-cyan-300',
  'pause':              'bg-gray-500/20   text-gray-400',
};

function InflectionsTab({ profile, profileId, refetch }) {
  const [form, setForm] = useState({ sampleId: '', segmentStart: 0, segmentEnd: 0, label: 'rapport', notes: '' });

  const tagMutation = useMutation({
    mutationFn: () => apiPost(`/voice-profiles/${profileId}/tag-inflection`, form),
    onSuccess: () => { toast.success('Tag saved'); refetch(); setForm(f => ({ ...f, notes: '' })); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save tag'),
  });

  const deleteMutation = useMutation({
    mutationFn: (tagId) => api.delete(`/voice-profiles/${profileId}/inflection/${tagId}`),
    onSuccess: () => { refetch(); },
  });

  const tags = profile.inflectionTags || [];
  const samples = profile.samples || [];

  return (
    <div className="space-y-5">
      <p className="text-sm text-white/50">
        Mark significant moments in your voice samples — rapport building, objection handling,
        closing patterns, etc. These annotations help understand your agent&apos;s speaking style.
      </p>

      {/* Add tag form */}
      <div className="panel p-5 space-y-4">
        <h3 className="font-semibold text-white text-sm flex items-center gap-2">
          <Tag className="w-4 h-4 text-civix-purple" /> Add Inflection Tag
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Sample</label>
            <select className="input w-full" value={form.sampleId} onChange={e => setForm(f => ({ ...f, sampleId: e.target.value }))}>
              <option value="">Select sample...</option>
              {samples.map(s => (
                <option key={s._id} value={s._id}>
                  [{s.speakerLabel}] {s.transcript?.slice(0, 40) || s._id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Label</label>
            <select className="input w-full" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}>
              {INFLECTION_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Start (sec)</label>
            <input type="number" className="input w-full" min={0} value={form.segmentStart}
              onChange={e => setForm(f => ({ ...f, segmentStart: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="form-label">End (sec)</label>
            <input type="number" className="input w-full" min={0} value={form.segmentEnd}
              onChange={e => setForm(f => ({ ...f, segmentEnd: parseFloat(e.target.value) || 0 }))} />
          </div>
        </div>

        <div>
          <label className="form-label">Notes</label>
          <input className="input w-full" placeholder="Optional description" value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        <button
          onClick={() => tagMutation.mutate()}
          disabled={!form.label || !form.sampleId || tagMutation.isPending}
          className="btn-primary flex items-center gap-2"
        >
          {tagMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
          Save Tag
        </button>
      </div>

      {/* Tag list */}
      {tags.length === 0 ? (
        <div className="text-center py-8 text-white/40">No inflection tags yet.</div>
      ) : (
        <div className="space-y-2">
          {tags.map(t => (
            <div key={t._id} className="flex items-center gap-3 panel p-3">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${INFLECTION_COLORS[t.label] || 'bg-white/10 text-white/50'}`}>
                {t.label}
              </span>
              <span className="text-xs text-white/40 font-mono">
                {t.segmentStart?.toFixed(1)}s → {t.segmentEnd?.toFixed(1)}s
              </span>
              <span className="text-sm text-white/70 flex-1">{t.notes || '—'}</span>
              <button onClick={() => deleteMutation.mutate(t._id)} className="text-white/30 hover:text-red-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Clone tab ────────────────────────────────────────────────────────────────

function CloneTab({ profile, profileId, refetch, qc }) {
  const [testText, setTestText] = useState('Hello! This is a test of my cloned voice. How does it sound?');
  const [testBusy, setTestBusy] = useState(false);
  const [cloneType, setCloneType] = useState(profile.cloneType || 'ivc');

  const buildMutation = useMutation({
    mutationFn: () => apiPost(`/voice-profiles/${profileId}/create-clone`, { cloneType }),
    onSuccess: () => {
      toast.success('Clone build started!');
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Clone failed'),
  });

  const regenMutation = useMutation({
    mutationFn: () => apiPost(`/voice-profiles/${profileId}/regenerate`, {}),
    onSuccess: () => { toast.success('Regeneration started'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Regeneration failed'),
  });

  async function testVoice() {
    if (!profile.elevenLabsVoiceId) return toast.error('No voice ID yet — build the clone first');
    setTestBusy(true);
    try {
      const res = await api.post(
        '/voice-cloning/test',
        { text: testText, voiceId: profile.elevenLabsVoiceId },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      new Audio(url).play();
      toast.success('Playing cloned voice!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'TTS test failed');
    } finally {
      setTestBusy(false);
    }
  }

  const includedCount = (profile.samples || []).filter(s => s.includedInClone).length;

  return (
    <div className="space-y-5">
      {/* Status card */}
      {profile.cloneStatus === 'ready' ? (
        <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-green-400">Voice Clone Ready</div>
            <div className="text-xs text-white/50">ElevenLabs Voice ID: {profile.elevenLabsVoiceId}</div>
          </div>
        </div>
      ) : profile.cloneStatus === 'failed' ? (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <XCircle className="w-5 h-5 text-red-400 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-red-400">Clone Failed</div>
            <div className="text-xs text-white/50">{profile.errorMessage || 'Unknown error'}</div>
          </div>
        </div>
      ) : null}

      {/* Clone type selection */}
      <div className="panel p-5 space-y-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-civix-purple" /> Build Voice Clone
        </h3>

        <div className="text-sm text-white/60">
          {includedCount} samples included in clone training.
          {includedCount === 0 && (
            <span className="text-yellow-400 ml-1">Include at least 1 sample in the Samples tab first.</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            { type: 'ivc', name: 'Instant Voice Clone', desc: 'Fast, works with ≤25 samples', warn: null },
            { type: 'pvc', name: 'Professional Clone', desc: '3+ hours of audio, highest fidelity', warn: 'PVC uses 1 slot (1/1 used on this account)' },
          ].map(({ type, name, desc, warn }) => (
            <button
              key={type}
              onClick={() => setCloneType(type)}
              className={`p-3 rounded-xl border text-sm text-left transition-all ${
                cloneType === type
                  ? 'border-civix-purple bg-civix-purple/20 text-white'
                  : 'border-civix-border text-white/50 hover:border-civix-purple/40'
              }`}
            >
              <div className="font-semibold">{name}</div>
              <div className="text-xs opacity-70 mt-0.5">{desc}</div>
              {warn && <div className="text-xs text-yellow-400 mt-1">{warn}</div>}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => buildMutation.mutate()}
            disabled={includedCount === 0 || buildMutation.isPending || profile.cloneStatus === 'training'}
            className="btn-primary flex items-center gap-2"
          >
            {buildMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Build {cloneType.toUpperCase()} Clone
          </button>

          {profile.cloneStatus === 'ready' && (
            <button
              onClick={() => regenMutation.mutate()}
              disabled={regenMutation.isPending}
              className="btn-ghost flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" /> Regenerate
            </button>
          )}
        </div>
      </div>

      {/* Test voice */}
      {profile.cloneStatus === 'ready' && profile.elevenLabsVoiceId && (
        <div className="panel p-5 space-y-3">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-civix-purple" /> Test Voice
          </h3>
          <textarea
            value={testText}
            onChange={e => setTestText(e.target.value)}
            rows={3}
            className="input w-full"
            placeholder="Enter text to synthesize..."
          />
          <button
            onClick={testVoice}
            disabled={testBusy || !testText.trim()}
            className="btn-primary flex items-center gap-2"
          >
            {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Play My Clone
          </button>
        </div>
      )}
    </div>
  );
}

// ─── A/B compare tab ─────────────────────────────────────────────────────────

function ABTab({ profile, profileId, refetch }) {
  const [scriptText, setScriptText] = useState('Hello! I\'m calling today to discuss how we can help protect your family\'s financial future. Do you have a moment?');
  const [voiceAId, setVoiceAId] = useState(profile.elevenLabsVoiceId || '');
  const [voiceBId, setVoiceBId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [audioA, setAudioA] = useState(null);
  const [audioB, setAudioB] = useState(null);
  const [judgeNotes, setJudgeNotes] = useState('');

  const { data: voicesData } = useQuery({
    queryKey: ['eleven-voices'],
    queryFn:  () => apiGet('/voice-profiles/meta/available-voices'),
    staleTime: 60000,
  });
  const voices = voicesData?.data || [];

  const charCount = scriptText.length;
  const estCost   = (charCount * 2 * 0.000030).toFixed(5);

  const runMutation = useMutation({
    mutationFn: () => apiPost(`/voice-profiles/${profileId}/ab-test`, { scriptText, voiceAId, voiceBId }),
    onSuccess: (r) => {
      const d = r?.data || r;
      setAudioA(d.voiceAResultUrl);
      setAudioB(d.voiceBResultUrl);
      setJudgeNotes(d.judgeNotes || '');
      toast.success('A/B test complete!');
      setConfirmed(false);
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'A/B test failed'),
  });

  const winnerMutation = useMutation({
    mutationFn: ({ testId, winnerVoiceId }) =>
      apiPost(`/voice-profiles/${profileId}/ab-test/${testId}/winner`, { winnerVoiceId }),
    onSuccess: () => { toast.success('Winner saved!'); refetch(); },
  });

  const pastTests = profile.abTests || [];

  function playAudio(dataUri) {
    const audio = new Audio(dataUri);
    audio.play().catch(() => toast.error('Audio play failed'));
  }

  return (
    <div className="space-y-5">
      <div className="panel p-5 space-y-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-civix-purple" /> A/B Voice Comparison
        </h3>

        <div>
          <label className="form-label">Script to synthesize</label>
          <textarea rows={4} className="input w-full" value={scriptText}
            onChange={e => { setScriptText(e.target.value); setConfirmed(false); }} />
          <p className="text-xs text-white/30 mt-1">
            {charCount} chars × 2 voices ≈ <span className="text-yellow-400">${estCost} estimated</span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Voice A (ID)</label>
            <input className="input w-full" placeholder="ElevenLabs voice ID"
              value={voiceAId} onChange={e => setVoiceAId(e.target.value)} />
            {profile.elevenLabsVoiceId && (
              <button
                className="text-xs text-civix-cyan mt-1 hover:underline"
                onClick={() => setVoiceAId(profile.elevenLabsVoiceId)}
              >
                Use this profile&apos;s clone
              </button>
            )}
          </div>
          <div>
            <label className="form-label">Voice B (ID)</label>
            <select className="input w-full" value={voiceBId} onChange={e => setVoiceBId(e.target.value)}>
              <option value="">Select voice...</option>
              {voices.map(v => (
                <option key={v.voice_id} value={v.voice_id}>{v.name} ({v.category})</option>
              ))}
            </select>
          </div>
        </div>

        {!confirmed ? (
          <button
            onClick={() => setConfirmed(true)}
            disabled={!scriptText || !voiceAId || !voiceBId}
            className="btn-secondary flex items-center gap-2"
          >
            <FlaskConical className="w-4 h-4" /> Confirm &amp; Generate (costs ~${estCost})
          </button>
        ) : (
          <div className="flex gap-3">
            <button onClick={() => setConfirmed(false)} className="btn-ghost">Cancel</button>
            <button
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {runMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Generate A/B Test
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {(audioA || audioB) && (
        <div className="panel p-5 space-y-4">
          <h3 className="font-semibold text-white">Results</h3>
          <div className="grid grid-cols-2 gap-4">
            {[{ label: 'Voice A', url: audioA, id: voiceAId }, { label: 'Voice B', url: audioB, id: voiceBId }].map(({ label, url, id }) => (
              <div key={label} className="bg-civix-panelLight rounded-xl p-4 space-y-2">
                <div className="text-sm font-medium text-white">{label}</div>
                <div className="text-xs text-white/40 font-mono">{id?.slice(0, 20)}...</div>
                <button
                  onClick={() => playAudio(url)}
                  className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                >
                  <Play className="w-4 h-4" /> Play
                </button>
              </div>
            ))}
          </div>
          {judgeNotes && (
            <div className="bg-civix-purple/10 border border-civix-purple/30 rounded-xl p-3 text-sm text-white/70">
              <span className="font-semibold text-civix-cyan">AI Judge: </span>{judgeNotes}
            </div>
          )}
        </div>
      )}

      {/* Past tests */}
      {pastTests.length > 0 && (
        <div>
          <h3 className="font-semibold text-white mb-3 text-sm">Past A/B Tests</h3>
          <div className="space-y-2">
            {pastTests.map(t => (
              <div key={t._id} className="panel p-3 text-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 truncate">{t.scriptText?.slice(0, 60)}...</span>
                  {t.winnerVoiceId && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Winner picked
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {[
                    { id: t.voiceAVoiceId, url: t.voiceAResultUrl, label: 'A' },
                    { id: t.voiceBVoiceId, url: t.voiceBResultUrl, label: 'B' },
                  ].map(({ id, url, label }) => url && (
                    <button
                      key={label}
                      onClick={() => playAudio(url)}
                      className="flex items-center gap-1 text-xs text-civix-cyan hover:underline"
                    >
                      <Play className="w-3 h-3" /> Voice {label}
                    </button>
                  ))}
                  {!t.winnerVoiceId && t.voiceAVoiceId && t.voiceBVoiceId && (
                    <>
                      <button onClick={() => winnerMutation.mutate({ testId: t._id, winnerVoiceId: t.voiceAVoiceId })}
                        className="text-xs text-white/40 hover:text-green-400">Pick A</button>
                      <button onClick={() => winnerMutation.mutate({ testId: t._id, winnerVoiceId: t.voiceBVoiceId })}
                        className="text-xs text-white/40 hover:text-green-400">Pick B</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Attach to bot tab ────────────────────────────────────────────────────────

function AttachTab({ profile, profileId, refetch }) {
  const { data: botsData } = useQuery({
    queryKey: ['bots-for-voice'],
    queryFn:  () => apiGet('/bots'),
    staleTime: 30000,
  });
  const bots = botsData?.data || botsData || [];
  const attachedIds = (profile.attachedBotIds || []).map(String);

  const attachMutation = useMutation({
    mutationFn: (botId) => apiPost(`/voice-profiles/${profileId}/attach-bot/${botId}`, {}),
    onSuccess: () => { toast.success('Voice profile attached to bot'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Attach failed'),
  });

  const detachMutation = useMutation({
    mutationFn: (botId) => api.delete(`/voice-profiles/${profileId}/attach-bot/${botId}`),
    onSuccess: () => { toast.success('Detached from bot'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Detach failed'),
  });

  if (profile.cloneStatus !== 'ready') {
    return (
      <div className="panel p-8 text-center space-y-2">
        <Sparkles className="w-8 h-8 text-civix-purple/50 mx-auto" />
        <p className="text-white/50 text-sm">Build the voice clone first (go to Clone tab).</p>
        <p className="text-white/30 text-xs">Status: {profile.cloneStatus}</p>
      </div>
    );
  }

  const voiceBots = bots.filter(b => b.type === 'voice' || !b.type);

  return (
    <div className="space-y-4">
      <p className="text-sm text-white/50">
        Select voice bots to use this clone. When a bot with an attached profile dials, your cloned voice speaks.
      </p>

      {voiceBots.length === 0 ? (
        <div className="panel p-8 text-center text-white/40">No voice bots found.</div>
      ) : (
        <div className="space-y-2">
          {voiceBots.map(bot => {
            const isAttached = attachedIds.includes(String(bot._id));
            return (
              <div key={bot._id} className="panel p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-civix-purple/20 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-civix-purple" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-white">{bot.name}</div>
                  <div className="text-xs text-white/40">{bot.status}</div>
                </div>
                {isAttached ? (
                  <button
                    onClick={() => detachMutation.mutate(bot._id)}
                    disabled={detachMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/30 transition-all"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Attached
                  </button>
                ) : (
                  <button
                    onClick={() => attachMutation.mutate(bot._id)}
                    disabled={attachMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-civix-border text-white/50 hover:bg-civix-purple/20 hover:text-white transition-all"
                  >
                    <Plus className="w-3 h-3" /> Attach
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {attachedIds.length > 0 && (
        <div className="mt-2 text-xs text-white/40 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Attached to {attachedIds.length} bot{attachedIds.length > 1 ? 's' : ''}.
          AI Dialer will use voice ID: {profile.elevenLabsVoiceId?.slice(0, 16)}...
        </div>
      )}
    </div>
  );
}

// ─── Root page ────────────────────────────────────────────────────────────────

export default function VoiceCloningStudio({ embedded = false } = {}) {
  const [view, setView] = useState('list');   // 'list' | 'new' | 'detail'
  const [profileId, setProfileId] = useState(null);

  function handleSelect(id) {
    setProfileId(id);
    setView('detail');
  }

  function handleNew() {
    setView('new');
  }

  function handleCreated(id) {
    setProfileId(id);
    setView('detail');
  }

  function handleBack() {
    setProfileId(null);
    setView('list');
  }

  return (
    <div className="space-y-4">
      {!embedded && (
        <TopBar
          title="Voice Cloning Studio"
          subtitle="AI Dialer · Clone your agent's voice from real calls"
          actions={
            view !== 'list' && view !== 'new' ? (
              <button onClick={handleBack} className="btn-ghost text-sm">
                ← All Profiles
              </button>
            ) : null
          }
        />
      )}
      {embedded && view !== 'list' && view !== 'new' && (
        <div className="flex justify-end">
          <button onClick={handleBack} className="btn-ghost text-sm">
            ← All Profiles
          </button>
        </div>
      )}

      {view === 'list'   && <ProfileList  onSelect={handleSelect} onNew={handleNew} />}
      {view === 'new'    && <NewProfileForm onCreated={handleCreated} onCancel={handleBack} />}
      {view === 'detail' && <ProfileDetail profileId={profileId} onBack={handleBack} />}
    </div>
  );
}
